import { create } from 'zustand';

export type WorkspaceAgentAssetListSavedBoard = {
  boardId: string;
  boardTitle: string;
};

export type WorkspaceAgentAssetListPresentation = 'gallery' | 'table';

export type WorkspaceAgentAssetListViewState = {
  activeArtifactId: string | null;
  activeSessionId: string | null;
  /** Ids the user just closed. A stale `artifactId` in the URL must not open them again. */
  dismissedArtifactIds: Record<string, true>;
  pinnedItemsOpen: boolean;
  /** Table or gallery for the artifact that is open. Starts as table for each artifact. */
  presentation: WorkspaceAgentAssetListPresentation;
  savedBoardsByArtifactId: Record<string, WorkspaceAgentAssetListSavedBoard>;
  tabsBySessionId: Record<string, string[]>;
  titlesByArtifactId: Record<string, string>;
};

export type WorkspaceAgentAssetListViewActions = {
  closeArtifactTab: (sessionId: string, artifactId: string) => string | null;
  closeArtifactView: () => void;
  markSavedAsBoard: (artifactId: string, board: WorkspaceAgentAssetListSavedBoard) => void;
  openArtifactTab: (params: { artifactId: string; reopen?: boolean; sessionId: string; title?: string }) => void;
  releaseDismissedArtifactIds: () => void;
  registerArtifactTitle: (artifactId: string, title: string) => void;
  resetArtifactView: () => void;
  setPinnedItemsOpen: (open: boolean | ((prev: boolean) => boolean)) => void;
  setPresentation: (presentation: WorkspaceAgentAssetListPresentation) => void;
};

export type WorkspaceAgentAssetListViewStore = WorkspaceAgentAssetListViewState & WorkspaceAgentAssetListViewActions;

const initialState: WorkspaceAgentAssetListViewState = {
  activeArtifactId: null,
  activeSessionId: null,
  dismissedArtifactIds: {},
  pinnedItemsOpen: false,
  presentation: 'table',
  savedBoardsByArtifactId: {},
  tabsBySessionId: {},
  titlesByArtifactId: {},
};

export const useWorkspaceAgentAssetListViewStore = create<WorkspaceAgentAssetListViewStore>()((set, get) => ({
  ...initialState,

  openArtifactTab: ({ artifactId, reopen = false, sessionId, title }) => {
    set((state) => {
      const isDismissed = Object.hasOwn(state.dismissedArtifactIds, artifactId);

      if (isDismissed && !reopen) {
        return state;
      }

      const dismissedArtifactIds = isDismissed
        ? Object.fromEntries(Object.entries(state.dismissedArtifactIds).filter(([id]) => id !== artifactId))
        : state.dismissedArtifactIds;
      const existingTabs = state.tabsBySessionId[sessionId] ?? [];
      const nextTabs = existingTabs.includes(artifactId) ? existingTabs : [...existingTabs, artifactId];
      const nextTitles =
        title && state.titlesByArtifactId[artifactId] !== title
          ? { ...state.titlesByArtifactId, [artifactId]: title }
          : state.titlesByArtifactId;

      return {
        activeArtifactId: artifactId,
        activeSessionId: sessionId,
        dismissedArtifactIds,
        presentation: state.activeArtifactId === artifactId ? state.presentation : 'table',
        tabsBySessionId:
          nextTabs === existingTabs ? state.tabsBySessionId : { ...state.tabsBySessionId, [sessionId]: nextTabs },
        titlesByArtifactId: nextTitles,
      };
    });
  },

  closeArtifactTab: (sessionId, artifactId) => {
    const current = get();
    const existingTabs = current.tabsBySessionId[sessionId] ?? [];
    const nextTabs = existingTabs.filter((id) => id !== artifactId);
    const isClosingActive = current.activeArtifactId === artifactId && current.activeSessionId === sessionId;
    const fallbackArtifactId = isClosingActive ? (nextTabs.at(-1) ?? null) : current.activeArtifactId;

    set((state) => ({
      activeArtifactId: fallbackArtifactId,
      activeSessionId: fallbackArtifactId ? state.activeSessionId : null,
      dismissedArtifactIds: { ...state.dismissedArtifactIds, [artifactId]: true },
      tabsBySessionId: { ...state.tabsBySessionId, [sessionId]: nextTabs },
    }));

    return fallbackArtifactId;
  },

  closeArtifactView: () => {
    set((state) => {
      const dismissedArtifactIds = { ...state.dismissedArtifactIds };

      if (state.activeArtifactId) {
        dismissedArtifactIds[state.activeArtifactId] = true;
      }

      const tabsBySessionId = state.activeSessionId
        ? { ...state.tabsBySessionId, [state.activeSessionId]: [] }
        : state.tabsBySessionId;

      return {
        activeArtifactId: null,
        activeSessionId: null,
        dismissedArtifactIds,
        tabsBySessionId,
      };
    });
  },

  registerArtifactTitle: (artifactId, title) => {
    if (!title) {
      return;
    }

    set((state) => {
      if (state.titlesByArtifactId[artifactId] === title) {
        return state;
      }

      return {
        titlesByArtifactId: {
          ...state.titlesByArtifactId,
          [artifactId]: title,
        },
      };
    });
  },

  releaseDismissedArtifactIds: () => {
    set((state) => {
      if (Object.keys(state.dismissedArtifactIds).length === 0) {
        return state;
      }

      return { dismissedArtifactIds: {} };
    });
  },

  markSavedAsBoard: (artifactId, board) => {
    set((state) => ({
      savedBoardsByArtifactId: {
        ...state.savedBoardsByArtifactId,
        [artifactId]: board,
      },
    }));
  },

  setPinnedItemsOpen: (open) => {
    set((state) => ({
      pinnedItemsOpen: typeof open === 'function' ? open(state.pinnedItemsOpen) : open,
    }));
  },

  setPresentation: (presentation) => {
    set({ presentation });
  },

  resetArtifactView: () => {
    set(initialState);
  },
}));
